import React, { useCallback, useEffect, useState } from "react";
import { Formik, FormikHelpers } from "formik";
import _ from "lodash";
import * as yup from "yup";

import theme from "theme";
import { colors } from "theme/palette";

import Box from "components/Box";
import Checkbox from "components/Checkbox";
import FormControlLabel from "components/FormControlLabel";
import Loading from "components/Loading";
import Select from "components/Select";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";

import AIAnswerSection from "entities/MemoTemplate/components/AIAnswerSectionSelector";
import DDSectionSelector from "entities/MemoTemplate/components/DDSectionSelector";
import MemoTemplateSectionSelector from "entities/MemoTemplate/components/SectionSelector";
import MemoTemplateSectionsSectionWrapper from "entities/MemoTemplate/components/SectionWrapper";
import TableBuilder from "entities/MemoTemplate/components/TableBuilder";
import {
  EMemoSectionType,
  EMemoSourcesAndUsesSectionDisplayMode,
} from "entities/MemoTemplate/constants";
import { IMemoTemplateSection } from "entities/MemoTemplate/sdk";

interface IAIPromptSectionValue {
  title?: string;
  prompt?: string;
  source_document_ids?: Array<number>;
  id?: number;
}

const VALIDATION_SCHEMA = yup.object({
  title: yup.string().required("The name is required!"),
  section_type: yup.string().required("The type is required!"),
  ai_prompt: yup
    .object()
    .default(null)
    .nullable()
    .shape({
      id: yup.number().nullable(),
      title: yup.string().when("id", {
        is: (id: number) => {
          return _.isNil(id);
        },
        then: (schema) => schema.required("Title field is required!"),
      }),
      prompt: yup.string().when("id", {
        is: (id: number) => _.isNil(id),
        then: (schema) => schema.required("Prompt field is required!"),
      }),
      source_document_ids: yup.array(),
    })
    .when("section_type", {
      is: (section_type: string) => section_type === "ai_answer",
      then: (schema) => schema.required("Please select an existing prompt!"),
    }),
});
interface ISectionForm {
  initialValues?: Partial<SectionValuesType>;
  onSubmit: (data: SectionValuesType) => Promise<any>;
  onCancel?: () => void;
  isEditForm?: boolean; // if the form is edit -> only the name can be changed because there might be information entered already for this section
  dueDiligenceSection?: IMemoTemplateSection["due_diligence_section"];
  formikRef?: React.Ref<any>;
}

const initialValues = {
  title: "New Section",
  section_type: EMemoSectionType.DUE_DILIGENCE,
  due_diligence_section: null,
  break_before: false,
  limit_image_section_height: true,
  table_section_compact_view: false,
  amenities_section_display_image: true,
  sources_and_uses_section_display_mode:
    EMemoSourcesAndUsesSectionDisplayMode.MIXED,
  checklist_table: [],
  checklist_table_editable_cells: [],
};

export type SectionValuesType = {
  title: string;
  section_type: EMemoSectionType;
  break_before: boolean;
  limit_image_section_height: boolean;
  table_section_compact_view: boolean;
  sources_and_uses_section_display_mode: EMemoSourcesAndUsesSectionDisplayMode;
  due_diligence_section: null | number;
  checklist_table: Array<Array<string>>;
  checklist_table_editable_cells: Array<{ row: number; column: number }>;
  amenities_section_display_image: boolean;
  ai_prompt?: IAIPromptSectionValue;
  due_diligence_package_id?: number;
};

const SectionForm = ({
  onSubmit,
  initialValues: propsInitialValues = {},
  isEditForm = false,
  dueDiligenceSection,
  formikRef,
}: ISectionForm) => {
  const [section, setSection] = useState<any>(null);

  useEffect(() => setSection(dueDiligenceSection), [dueDiligenceSection]);

  const handleSubmit = useCallback(
    (
      values: SectionValuesType,
      formikHelpers: FormikHelpers<SectionValuesType>
    ) =>
      onSubmit({ ...initialValues, ...values }).catch(formikHelpers.setErrors),
    [onSubmit]
  );

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{ ...initialValues, ...propsInitialValues }}
      onSubmit={handleSubmit}
      validationSchema={VALIDATION_SCHEMA}
    >
      {(formik) => {
        return (
          <Stack
            spacing={2}
            justifyContent="space-between"
            sx={{ height: "100%" }}
          >
            <Loading open={formik.isSubmitting} />

            <Stack display="grid" gap={4} gridTemplateColumns="1fr 2fr">
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Name"
                  {...formik.getFieldProps("title")}
                  error={formik.touched.title && !!formik.errors.title}
                  helperText={formik.touched.title && formik.errors.title}
                />
                <MemoTemplateSectionSelector
                  disabled={isEditForm}
                  value={formik.values.section_type}
                  onChange={(value) => {
                    if (value === EMemoSectionType.AI_ANSWER) {
                      formik.setFieldValue("ai_prompt", null);
                    }

                    if (value !== EMemoSectionType.CHECKLIST_TABLE) {
                      formik.setFieldValue("checklist_table", []);
                    }

                    formik.setFieldValue("section_type", value);
                  }}
                />
              </Stack>
              <Stack spacing={2} overflow="hidden">
                <Text variant="h3">Preview</Text>
                {formik.values.section_type === EMemoSectionType.AI_ANSWER && (
                  <AIAnswerSection formik={formik} />
                )}
                <Box
                  sx={{
                    border: `1px dashed ${colors.blue20}`,
                    padding: theme.spacing(2),
                  }}
                >
                  <MemoTemplateSectionsSectionWrapper
                    section={
                      {
                        section_type: formik.values.section_type,
                        due_diligence_section: section,
                        title: formik.values.title || section?.name,
                        sources_and_uses_section_display_mode:
                          formik.values.sources_and_uses_section_display_mode,
                        amenities_section_display_image:
                          formik.values.amenities_section_display_image,
                        checklist_table: formik.values.checklist_table.map(
                          (row) =>
                            row.map((value) => ({
                              value,
                            }))
                        ),
                      } as IMemoTemplateSection
                    }
                  />
                </Box>
                {formik.values.section_type ===
                  EMemoSectionType.CHECKLIST_TABLE && (
                  <TableBuilder
                    table={formik.values.checklist_table}
                    onChange={({ table }) =>
                      formik.setFieldValue("checklist_table", table)
                    }
                    editableCells={formik.values.checklist_table_editable_cells}
                    onEditableCellsChange={({ editableCells }) =>
                      formik.setFieldValue(
                        "checklist_table_editable_cells",
                        editableCells
                      )
                    }
                  />
                )}
                <FormControlLabel
                  label={<Text variant="label">Start on a new page</Text>}
                  control={
                    <Checkbox
                      checked={formik.values.break_before}
                      onChange={(event, checked) =>
                        formik.setFieldValue("break_before", checked)
                      }
                    />
                  }
                />

                {formik.values.section_type ===
                  EMemoSectionType.SOURCE_AND_USES && (
                  <Select
                    label="Display mode"
                    options={[
                      {
                        value: EMemoSourcesAndUsesSectionDisplayMode.MIXED,
                        label: "Mixed",
                      },
                      {
                        value:
                          EMemoSourcesAndUsesSectionDisplayMode.ONLY_CONSTRUCTION,
                        label: "Only construction",
                      },
                      {
                        value:
                          EMemoSourcesAndUsesSectionDisplayMode.ONLY_PERMANENT,
                        label: "Only permanent",
                      },
                    ]}
                    value={formik.values.sources_and_uses_section_display_mode}
                    onChange={(event) =>
                      formik.setFieldValue(
                        "sources_and_uses_section_display_mode",
                        event.target.value
                      )
                    }
                  />
                )}
                {formik.values.section_type === EMemoSectionType.IMAGE && (
                  <FormControlLabel
                    label={<Text variant="label">Limit image height</Text>}
                    control={
                      <Checkbox
                        checked={formik.values.limit_image_section_height}
                        onChange={(event, checked) =>
                          formik.setFieldValue(
                            "limit_image_section_height",
                            checked
                          )
                        }
                      />
                    }
                  />
                )}
                {[
                  EMemoSectionType.EXCEL_TABLE,
                  EMemoSectionType.DUE_DILIGENCE,
                  EMemoSectionType.CHECKLIST_TABLE,
                ].includes(formik.values.section_type) && (
                  <FormControlLabel
                    label={
                      <Text variant="label">Display table in compact view</Text>
                    }
                    control={
                      <Checkbox
                        checked={formik.values.table_section_compact_view}
                        onChange={(event, checked) =>
                          formik.setFieldValue(
                            "table_section_compact_view",
                            checked
                          )
                        }
                      />
                    }
                  />
                )}
                {[
                  EMemoSectionType.UNIT_AMENITIES,
                  EMemoSectionType.COMMUNITY_AMENITIES,
                ].includes(formik.values.section_type) && (
                  <FormControlLabel
                    label={<Text variant="label">Display image</Text>}
                    control={
                      <Checkbox
                        checked={formik.values.amenities_section_display_image}
                        onChange={(event, checked) =>
                          formik.setFieldValue(
                            "amenities_section_display_image",
                            checked
                          )
                        }
                      />
                    }
                  />
                )}
                {formik.values.section_type ===
                  EMemoSectionType.DUE_DILIGENCE && (
                  <DDSectionSelector
                    value={formik.values.due_diligence_section}
                    due_diligence_package_id={
                      propsInitialValues.due_diligence_package_id
                    }
                    isEditForm={isEditForm}
                    onChange={(section) => {
                      formik.setFieldValue(
                        "due_diligence_section",
                        section.section_id
                      );
                      setSection(section);
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        );
      }}
    </Formik>
  );
};

export default SectionForm;
